import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ColDef,
  ICellRendererParams,
  ModuleRegistry,
  themeQuartz,
} from 'ag-grid-community';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import {
  Button,
  ConfigProvider,
  DatePicker,
  Dropdown,
  Form,
  InputNumber,
  Modal,
  Select,
  Upload,
  message,
} from 'antd';
import type { MenuProps, UploadProps } from 'antd';
import dayjs from 'dayjs';
import TieringService from '../../services/TieringService';
import type {
  PartnerImportResultDto,
  PartnerOfPartnershipDto,
  PartnershipSummaryDto,
} from '../../types/tiering.types';
import { INTEREST_CLASSES } from '../../types/tiering.types';
import { usePartnershipContext } from '../../../../core/context/PartnershipContext';
import EntityService from '../../../EntityView/services/EntityService';
import type { EntityDto } from '../../../EntityView/services/EntityService';
import { ENV } from '../../../../config';
import { getAuthToken } from '../../../../core/services/SessionStorageService';
import { darkFormTheme, MODAL_STYLES, MODAL_TITLE_STYLE } from '../../../../styles/modalStyles';
import styles from './PartnerRoster.module.css';

ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule]);

const gridTheme = themeQuartz.withParams({
  rowBorder: { style: 'solid', width: 1, color: 'rgba(255, 255, 255, 0.3)' },
});

interface PartnerRosterProps {
  partnershipId: number;
}

const PartnerRoster: React.FC<PartnerRosterProps> = ({ partnershipId }) => {
  const { selectedClient } = usePartnershipContext();
  const navigate = useNavigate();

  const [rows, setRows] = useState<PartnerOfPartnershipDto[]>([]);
  const [summary, setSummary] = useState<PartnershipSummaryDto | null>(null);
  const [loading, setLoading] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editing, setEditing] = useState<PartnerOfPartnershipDto | null>(null);
  const [saving, setSaving] = useState(false);
  const [entities, setEntities] = useState<EntityDto[]>([]);
  const [addForm] = Form.useForm();
  const watchedOwnership = Form.useWatch('ownershipPercent', addForm);

  const [showImportModal, setShowImportModal] = useState(false);
  const [importResult, setImportResult] = useState<PartnerImportResultDto | null>(null);
  const [importing, setImporting] = useState(false);

  const [generating, setGenerating] = useState(false);

  const fetchData = useCallback(async () => {
    if (!partnershipId) return;
    setLoading(true);
    try {
      const [partners, summaryData] = await Promise.all([
        TieringService.getPartnersOfPartnership(partnershipId),
        TieringService.getPartnershipSummary(partnershipId),
      ]);
      setRows(partners);
      setSummary(summaryData);
    } catch {
      message.error('Failed to load partner roster');
    } finally {
      setLoading(false);
    }
  }, [partnershipId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!showAddModal || editing || !selectedClient?.id) return;
    EntityService.getEntitiesByClient(selectedClient.id)
      .then((data) => setEntities(data))
      .catch(() => setEntities([]));
  }, [showAddModal, editing, selectedClient]);

  const remainingCapacity = summary?.remainingCapacity ?? 100;

  const openAdd = useCallback(() => {
    setEditing(null);
    addForm.resetFields();
    addForm.setFieldsValue({ entryDate: dayjs() });
    setShowAddModal(true);
  }, [addForm]);

  const openEdit = useCallback(
    (row: PartnerOfPartnershipDto) => {
      setEditing(row);
      addForm.setFieldsValue({
        ownershipPercent: row.ownershipPercent,
        interestClass: row.interestClass,
      });
      setShowAddModal(true);
    },
    [addForm],
  );

  const confirmExit = useCallback(
    (row: PartnerOfPartnershipDto) => {
      Modal.confirm({
        title: 'Exit Partner',
        content: `Mark "${row.partnerName}" as exited from this partnership? This keeps the historical record but removes them from active ownership.`,
        okText: 'Exit Partner',
        okType: 'danger',
        onOk: async () => {
          try {
            await TieringService.exitPartner(row.relationshipId);
            message.success(`${row.partnerName} exited`);
            fetchData();
          } catch {
            message.error('Failed to exit partner');
          }
        },
      });
    },
    [fetchData],
  );

  const handleSaveAdd = async () => {
    const values = await addForm.validateFields();
    setSaving(true);
    try {
      if (editing) {
        await TieringService.updateOwnership(editing.relationshipId, {
          ownershipPercent: values.ownershipPercent as number,
          interestClass: values.interestClass as string,
        });
        message.success('Partner updated');
      } else {
        await TieringService.bulkAddPartners(partnershipId, [{
          partnerId: values.partnerId as number,
          ownershipPercent: values.ownershipPercent as number,
          interestClass: values.interestClass as string,
          entryDate: values.entryDate ? (values.entryDate as dayjs.Dayjs).toISOString() : undefined,
        }]);
        message.success('Partner added');
      }
      setShowAddModal(false);
      fetchData();
    } catch (err: unknown) {
      const description = (err as { response?: { data?: { description?: string } } })?.response?.data?.description;
      message.error(description ?? 'Failed to save partner');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateTiering = async () => {
    setGenerating(true);
    try {
      const result = await TieringService.generateTieringFromRoster(partnershipId);
      message.success(`Tiering structure updated — ${result.updated} relationships configured`);
      navigate(`/tiering/${partnershipId}`);
    } catch {
      message.error('Failed to generate tiering structure');
    } finally {
      setGenerating(false);
    }
  };

  const importUploadProps: UploadProps = {
    accept: '.xlsx',
    showUploadList: false,
    action: `${ENV.API_URL}/api/tiering/partners-of/${partnershipId}/import`,
    headers: {
      Authorization: `Bearer ${getAuthToken()}`,
      tenant: ENV.TENANT_ID ?? '',
    },
    beforeUpload: (file) => {
      if (!file.name.endsWith('.xlsx')) {
        message.error('Only .xlsx files are accepted');
        return Upload.LIST_IGNORE;
      }
      setImporting(true);
      setImportResult(null);
      return true;
    },
    onChange: (info) => {
      if (info.file.status === 'done') {
        setImporting(false);
        setImportResult(info.file.response as PartnerImportResultDto);
        fetchData();
      } else if (info.file.status === 'error') {
        setImporting(false);
        const errMsg = (info.file.response as { description?: string })?.description ?? 'Import failed';
        message.error(errMsg);
      }
    },
  };

  const colDefs: ColDef<PartnerOfPartnershipDto>[] = useMemo(
    () => [
      { field: 'partnerName', headerName: 'Partner Name', flex: 2 },
      { field: 'interestClass', headerName: 'Interest Class', flex: 1 },
      {
        field: 'ownershipPercent',
        headerName: 'Ownership %',
        flex: 1,
        cellClass: 'ag-right-aligned-cell',
        valueFormatter: (p) => (p.value != null ? `${Number(p.value).toFixed(2)}%` : ''),
      },
      {
        field: 'entryDate',
        headerName: 'Entry Date',
        flex: 1,
        valueFormatter: (p) => (p.value ? dayjs(p.value).format('MM/DD/YYYY') : ''),
      },
      {
        field: 'exitDate',
        headerName: 'Exit Date',
        flex: 1,
        valueFormatter: (p) => (p.value ? dayjs(p.value).format('MM/DD/YYYY') : ''),
      },
      {
        field: 'isActive',
        headerName: 'Status',
        flex: 1,
        cellRenderer: (params: ICellRendererParams<PartnerOfPartnershipDto>) => (
          <span className={params.value ? styles.badgeActive : styles.badgeExited}>
            {params.value ? 'Active' : 'Exited'}
          </span>
        ),
      },
      {
        headerName: 'Actions',
        width: 100,
        sortable: false,
        filter: false,
        cellRenderer: (params: ICellRendererParams<PartnerOfPartnershipDto>) => {
          if (!params.data) return null;
          const row = params.data;
          const items: MenuProps['items'] = [
            { key: 'edit', label: 'Edit', onClick: () => openEdit(row) },
            {
              key: 'exit',
              label: 'Exit Partner',
              danger: true,
              disabled: !row.isActive,
              onClick: () => confirmExit(row),
            },
          ];
          return (
            <Dropdown menu={{ items }} trigger={['click']}>
              <Button size="small" type="text" style={{ color: '#ADADAD' }}>
                ⋯
              </Button>
            </Dropdown>
          );
        },
      },
    ],
    [openEdit, confirmExit],
  );

  const defaultColDef: ColDef<PartnerOfPartnershipDto> = useMemo(
    () => ({ sortable: true, resizable: true, filter: true, minWidth: 100 }),
    [],
  );

  const capacityClass = remainingCapacity < 0
    ? styles.capacityOver
    : remainingCapacity === 0
      ? styles.capacityFull
      : styles.capacityAvailable;

  return (
    <div className={styles.wrapper}>
      <div className={styles.summaryBar}>
        <span>
          {summary?.partnerCount ?? 0} partners · {(summary?.totalOwnership ?? 0).toFixed(2)}% allocated ·{' '}
          <span className={capacityClass}>{remainingCapacity.toFixed(2)}% remaining</span>
        </span>
        <div className={styles.toolbarActions}>
          <Button onClick={() => { setImportResult(null); setShowImportModal(true); }}>
            Import Partners
          </Button>
          <Button onClick={handleGenerateTiering} loading={generating}>
            Generate Tiering Structure
          </Button>
          <button className={styles.btnAdd} onClick={openAdd}>
            + Add Partner
          </button>
        </div>
      </div>

      {loading ? (
        <div className={styles.loadingState}><span className={styles.spinner} /></div>
      ) : rows.length === 0 ? (
        <div className={styles.emptyState}>No partners in this roster yet</div>
      ) : (
        <div className={styles.grid}>
          <AgGridReact
            rowData={rows}
            columnDefs={colDefs}
            defaultColDef={defaultColDef}
            theme={gridTheme}
            rowSelection="single"
            domLayout="autoHeight"
          />
        </div>
      )}

      <Modal
        title={<span style={MODAL_TITLE_STYLE}>{editing ? 'Edit Partner' : 'Add Partner'}</span>}
        open={showAddModal}
        onOk={handleSaveAdd}
        onCancel={() => setShowAddModal(false)}
        okText={editing ? 'Save' : 'Add Partner'}
        okButtonProps={{ loading: saving }}
        styles={MODAL_STYLES}
      >
        <ConfigProvider theme={darkFormTheme}>
          <Form form={addForm} layout="vertical" className={styles.form}>
            {!editing && (
              <Form.Item
                name="partnerId"
                label="Partner"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Select
                  showSearch
                  placeholder="Search entities..."
                  optionFilterProp="label"
                  options={entities.map((e) => ({ value: e.id, label: `${e.name} (${e.entityType})` }))}
                  styles={{ popup: { root: { background: 'rgba(15,22,40,0.98)' } } }}
                />
              </Form.Item>
            )}
            <Form.Item
              name="interestClass"
              label="Interest Class"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Select
                placeholder="Select interest class"
                options={INTEREST_CLASSES.map((c) => ({ value: c, label: c }))}
              />
            </Form.Item>
            <Form.Item
              name="ownershipPercent"
              label="Ownership %"
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.01} precision={2} addonAfter="%" />
            </Form.Item>
            <div className={styles.capacityHint}>
              Remaining capacity: {(editing
                ? remainingCapacity + (editing.ownershipPercent ?? 0) - (watchedOwnership ?? 0)
                : remainingCapacity - (watchedOwnership ?? 0)
              ).toFixed(2)}%
            </div>
            {!editing && (
              <Form.Item name="entryDate" label="Entry Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            )}
          </Form>
        </ConfigProvider>
      </Modal>

      <Modal
        title={<span style={MODAL_TITLE_STYLE}>Import Partners</span>}
        open={showImportModal}
        onCancel={() => setShowImportModal(false)}
        footer={null}
        styles={MODAL_STYLES}
      >
        <p className={styles.importHint}>
          Upload a .xlsx file with columns: Entity Name | EIN | Interest Class | Ownership % | Entry Date
        </p>
        <Upload {...importUploadProps}>
          <Button loading={importing}>Choose File</Button>
        </Upload>
        {importResult && (
          <div className={styles.importResult}>
            <p>{importResult.created} partners added, {importResult.entitiesCreated} new entities created.</p>
            <p>Total ownership: {importResult.totalOwnership.toFixed(2)}%</p>
            {importResult.skipped.length > 0 && (
              <>
                <p className={styles.importSkippedTitle}>Skipped:</p>
                <ul className={styles.importSkippedList}>
                  {importResult.skipped.map((s) => <li key={s}>{s}</li>)}
                </ul>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PartnerRoster;
